import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Loader2, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface InlineQuoteFormProps {
  productName: string;
  productId: string;
  variantId?: string;
}

const InlineQuoteForm = ({ productName, productId, variantId }: InlineQuoteFormProps) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    quantity: "",
  });
  const [honeypot, setHoneypot] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (honeypot) {
      setIsSubmitted(true);
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke("submit-contact-form", {
        body: {
          ...formData,
          subject: `Quick Quote: ${productName}`,
          message: `Quick quote request for ${productName}${variantId ? ` (${variantId})` : ""}. Quantity: ${formData.quantity || "Not specified"}.`,
          honeypot,
        },
      });

      if (error) throw new Error(error.message);

      setIsSubmitted(true);
      toast({
        title: "Quote Requested!",
        description: "We'll send you pricing within 24 hours.",
      });
    } catch (err) {
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to submit. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <Card className="border-eco-green/30 bg-eco-green/5">
        <CardContent className="p-6 text-center">
          <CheckCircle className="w-10 h-10 text-eco-green mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-foreground mb-1">Quote Requested!</h3>
          <p className="text-sm text-muted-foreground">We'll get back to you within 24 hours with pricing for the {productName}.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
    >
      <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
        <CardContent className="p-6">
          <h3 className="text-lg font-bold text-foreground mb-1">Get a Quick Quote</h3>
          <p className="text-sm text-muted-foreground mb-4">
            For <span className="text-primary font-medium">{productName}</span> — pricing sent in 24h
          </p>

          <form onSubmit={handleSubmit} className="space-y-3">
            <Input
              placeholder="Your Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="bg-background/50 border-border/50"
            />
            <Input
              type="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="bg-background/50 border-border/50"
            />
            <div className="grid grid-cols-2 gap-3">
              <Input
                type="tel"
                placeholder="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="bg-background/50 border-border/50"
              />
              <Input
                type="text"
                placeholder="Quantity"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                className="bg-background/50 border-border/50"
              />
            </div>

            {/* Honeypot */}
            <div className="absolute -left-[9999px] opacity-0 pointer-events-none" aria-hidden="true">
              <Input type="text" name="website" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-eco-green to-teal-bright hover:from-eco-green-light hover:to-teal-bright text-white"
            >
              {isSubmitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" />Get Quote</>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default InlineQuoteForm;
